import { createDefaultRecognitionConfig } from './profiles.js';
import { migrateContentToV3 } from './content-migrate.js';
import { flattenToLegacyContent } from './flatten-legacy.js';
/**
 * Pure function that computes a {@link VersionDiff} between two `JobTypeVersionContent` payloads.
 *
 * Fields are matched by their stable `name`. Coarse-grained flags (`pipelineChanged`, etc.) use
 * structural equality via JSON serialization so any semantic change within those sub-trees is
 * reported.
 */
export function computeVersionDiff(beforeInput, afterInput) {
    // F-037 — tolerate V3 (per-form) content. Callers (e.g. the publish modal)
    // may pass raw stored content at schemaVersion 3, where fields/rules/export/
    // profiles live under `forms[]`, so the top-level arrays this differ reads are
    // undefined and `.map` throws (blank screen at publish). Normalize to the
    // legacy flattened view first. Idempotent for already-legacy V1/V2 content
    // (flattenToLegacyContent returns it unchanged), so V2 callers are unaffected.
    const before = flattenToLegacyContent(migrateContentToV3(beforeInput));
    const after = flattenToLegacyContent(migrateContentToV3(afterInput));
    const beforeMap = new Map(before.fields.map((f) => [f.name, f]));
    const afterMap = new Map(after.fields.map((f) => [f.name, f]));
    const fieldsAdded = after.fields.filter((f) => !beforeMap.has(f.name)).map((f) => f.name);
    const fieldsRemoved = before.fields.filter((f) => !afterMap.has(f.name)).map((f) => f.name);
    const fieldsChanged = [];
    for (const f of after.fields) {
        const prev = beforeMap.get(f.name);
        if (!prev)
            continue;
        const changedKeys = diffFieldKeys(prev, f);
        if (changedKeys.length)
            fieldsChanged.push({ name: f.name, changedKeys });
    }
    const { entries: rulesDiff, summary: rulesSummary } = diffRules(before.rules ?? [], after.rules ?? []);
    const exportChanged = !structuralEqual(before.export, after.export);
    const exportDiff = exportChanged
        ? diffExport(before.export?.destinations ?? [], after.export?.destinations ?? [])
        : undefined;
    return {
        fieldsAdded,
        fieldsRemoved,
        fieldsChanged,
        pipelineChanged: !structuralEqual(before.pipeline, after.pipeline),
        headerMappingChanged: !structuralEqual(before.headerMapping, after.headerMapping),
        rulesChanged: !structuralEqual(before.rules, after.rules),
        exportChanged,
        exportDiff,
        rulesDiff,
        summary: [
            ...diffProfiles(before.profiles ?? [], after.profiles ?? []),
            ...diffRecognition(before.formRecognition ?? createDefaultRecognitionConfig(), after.formRecognition ?? createDefaultRecognitionConfig()),
            ...rulesSummary,
        ],
    };
}
const FIELD_DIFF_KEYS = [
    'label',
    'dataType',
    'scope',
    'stageMap',
    'groupId',
    'displayOrder',
];
function diffFieldKeys(a, b) {
    const changed = [];
    for (const k of FIELD_DIFF_KEYS) {
        if (!structuralEqual(a[k], b[k]))
            changed.push(String(k));
    }
    return changed;
}
function structuralEqual(a, b) {
    return stableStringify(a) === stableStringify(b);
}
// Postgres jsonb does not preserve key insertion order, so content read back
// from the DB may have different key ordering than what was written. Sort keys
// recursively so structural equality ignores ordering.
function stableStringify(value) {
    return JSON.stringify(value, (_key, val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            return Object.keys(val)
                .sort()
                .reduce((acc, k) => {
                acc[k] = val[k];
                return acc;
            }, {});
        }
        return val;
    });
}
// Keys compared when determining what changed on a rule.
const RULE_DIFF_KEYS = [
    'name',
    'config',
    'condition',
    'trigger',
    'stage',
    'scope',
    'severity',
    'overrideBehavior',
    'isActive',
    'displayOrder',
    'type',
    'fieldRef',
];
function diffRules(before, after) {
    const entries = [];
    const summary = [];
    const beforeMap = new Map(before.map((r) => [r.id, r]));
    const afterMap = new Map(after.map((r) => [r.id, r]));
    // Added rules — in after but not in before
    for (const rule of after) {
        if (!beforeMap.has(rule.id)) {
            entries.push({ type: 'added', ruleId: rule.id, ruleName: rule.name });
            summary.push(`Added rule "${rule.name}"`);
        }
    }
    // Removed rules — in before but not in after
    for (const rule of before) {
        if (!afterMap.has(rule.id)) {
            entries.push({ type: 'removed', ruleId: rule.id, ruleName: rule.name });
            summary.push(`Removed rule "${rule.name}"`);
        }
    }
    // Changed rules — in both, compare keys
    for (const afterRule of after) {
        const beforeRule = beforeMap.get(afterRule.id);
        if (!beforeRule)
            continue;
        const changedKeys = [];
        for (const key of RULE_DIFF_KEYS) {
            if (!structuralEqual(beforeRule[key], afterRule[key])) {
                changedKeys.push(key);
            }
        }
        if (changedKeys.length > 0) {
            entries.push({
                type: 'changed',
                ruleId: afterRule.id,
                ruleName: afterRule.name,
                details: changedKeys.join(', '),
            });
            summary.push(`Changed rule "${afterRule.name}" (${changedKeys.join(', ')})`);
        }
    }
    return { entries, summary };
}
function diffProfiles(before, after) {
    const lines = [];
    const beforeById = new Map(before.map((p, i) => [p.id, { profile: p, index: i }]));
    for (const [idx, afterP] of after.entries()) {
        const beforeEntry = beforeById.get(afterP.id);
        if (!beforeEntry) {
            lines.push(`Added profile "${afterP.name}"`);
            continue;
        }
        const { profile: beforeP, index: beforeIdx } = beforeEntry;
        if (beforeP.name !== afterP.name) {
            lines.push(`Renamed profile "${beforeP.name}" → "${afterP.name}"`);
        }
        if (beforeIdx !== idx) {
            lines.push(`Reordered profile "${afterP.name}" (${beforeIdx} → ${idx})`);
        }
        const beforeStageMap = new Map(beforeP.stages.map((s) => [s.stage, s.enabled]));
        for (const stageConfig of afterP.stages) {
            const wasEnabled = beforeStageMap.get(stageConfig.stage);
            if (wasEnabled !== undefined && wasEnabled !== stageConfig.enabled) {
                const verb = stageConfig.enabled ? 'Enabled' : 'Disabled';
                lines.push(`${verb} ${stageConfig.stage} in profile "${afterP.name}"`);
            }
        }
    }
    const afterIds = new Set(after.map((p) => p.id));
    for (const { profile } of beforeById.values()) {
        if (!afterIds.has(profile.id)) {
            lines.push(`Removed profile "${profile.name}"`);
        }
    }
    return lines;
}
function diffExport(before, after) {
    const beforeMap = new Map(before.map((d) => [d.id, d]));
    const afterMap = new Map(after.map((d) => [d.id, d]));
    const destinationsAdded = after
        .filter((d) => !beforeMap.has(d.id))
        .map((d) => d.name);
    const destinationsRemoved = before
        .filter((d) => !afterMap.has(d.id))
        .map((d) => d.name);
    const destinationsChanged = [];
    for (const afterDest of after) {
        const beforeDest = beforeMap.get(afterDest.id);
        if (!beforeDest)
            continue;
        const changes = [];
        // Mappings
        if (!structuralEqual(beforeDest.mappings, afterDest.mappings)) {
            const count = afterDest.mappings.length;
            const prevCount = beforeDest.mappings.length;
            if (count !== prevCount) {
                changes.push(`${count} mapping(s) changed`);
            }
            else {
                changes.push(`${count} mapping(s) changed`);
            }
        }
        // Structure
        if (!structuralEqual(beforeDest.structure, afterDest.structure)) {
            changes.push('structure reorganized');
        }
        // Routing condition
        if (!structuralEqual(beforeDest.routingCondition, afterDest.routingCondition)) {
            if (!beforeDest.routingCondition && afterDest.routingCondition) {
                changes.push('routing condition added');
            }
            else if (beforeDest.routingCondition && !afterDest.routingCondition) {
                changes.push('routing condition removed');
            }
            else {
                changes.push('routing condition changed');
            }
        }
        // Schedule
        if (!structuralEqual(beforeDest.schedule, afterDest.schedule)) {
            changes.push('schedule changed');
        }
        // Adapter config (adapter type, format, config)
        if (beforeDest.adapter !== afterDest.adapter ||
            beforeDest.format !== afterDest.format ||
            !structuralEqual(beforeDest.config, afterDest.config)) {
            changes.push('adapter config changed');
        }
        if (changes.length > 0) {
            destinationsChanged.push({ name: afterDest.name, changes });
        }
    }
    return { destinationsAdded, destinationsRemoved, destinationsChanged };
}
function diffRecognition(before, after) {
    const lines = [];
    if (before.confidenceThreshold !== after.confidenceThreshold) {
        lines.push(`Recognition confidence threshold ${before.confidenceThreshold} → ${after.confidenceThreshold}`);
    }
    if (before.multiFormBatchPolicy !== after.multiFormBatchPolicy) {
        lines.push(`Recognition multi-form policy ${before.multiFormBatchPolicy} → ${after.multiFormBatchPolicy}`);
    }
    if (before.hints.length !== after.hints.length) {
        lines.push(`Recognition hints ${before.hints.length} → ${after.hints.length}`);
    }
    if (before.sampleTemplates.length !== after.sampleTemplates.length) {
        lines.push(`Recognition samples ${before.sampleTemplates.length} → ${after.sampleTemplates.length}`);
    }
    return lines;
}
//# sourceMappingURL=diff.js.map